import {
  Download,
  Loader2,
  Pause,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Play,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  CLIENT_COMMS_EVENTS,
  CLIENT_COMMS_PROPERTIES,
} from '@/constants/analytics/client-communications-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';
import { useGetAudioConversationDetailQuery } from '@/services/api/audioConversationService';
import { audioService } from '@/services/api/audioService';
import { useGetTranscriptSegmentsQuery } from '@/services/api/transcriptService';
import { UnifiedCommunication } from '@/services/types/client-communications-types';

import { useClientCommunications } from '../ClientCommunicationsContext';

interface CallDetailPaneProps {
  communication: UnifiedCommunication;
}

interface ArtifactDigest {
  title: string;
  work_log_entry: string;
  outcome?: string;
  next_steps?: string;
}

interface ConversationDetail {
  id: string;
  external_id: string | null;
  audio_path: string | null;
  artifact_digest: ArtifactDigest | null;
}

// Format time for transcript
const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Format duration for display
const formatDuration = (seconds?: number) => {
  if (!seconds) return null;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours} hr ${minutes} min`;
  }
  if (minutes > 0) {
    return `${minutes} min`;
  }
  return 'Less than 1 min';
};

// Format date/time for header
const formatDateTime = (timestamp: string) => {
  const date = new Date(timestamp);
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${dateStr} \u2022 ${timeStr}`;
};

// Transcript segment component
const TranscriptSegment: React.FC<{
  segment: {
    id: string;
    start_time: number;
    end_time: number;
    text: string;
    external_speaker_label?: string;
  };
  isClient: boolean;
}> = ({ segment, isClient }) => {
  return (
    <div
      className={cn(
        'mb-4 p-3 rounded-lg',
        isClient ? 'bg-blue-50/50' : 'bg-zinc-50',
      )}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className={cn(
            'text-sm font-bold',
            isClient ? 'text-blue-900' : 'text-primary',
          )}
        >
          {isClient ? 'Client' : 'Attorney'}
        </span>
        {isClient && (
          <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded uppercase">
            Client
          </span>
        )}
        <span className="text-xs font-semibold text-zinc-600">
          {formatTime(segment.start_time)}
        </span>
      </div>
      <p className="text-sm font-medium text-zinc-800 leading-relaxed">
        {segment.text}
      </p>
    </div>
  );
};

export const CallDetailPane: React.FC<CallDetailPaneProps> = ({
  communication,
}) => {
  const { matterId } = useClientCommunications();
  const capture = usePostHogCapture();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const conversationId = communication.audio_conversation_id || '';

  const { data: conversationDetailApi, isLoading: isLoadingDetail } =
    useGetAudioConversationDetailQuery(
      { matterId, conversationId },
      { skip: !conversationId },
    );

  const { data: transcriptSegmentsApi = [], isLoading: isLoadingTranscript } =
    useGetTranscriptSegmentsQuery(
      { matterId, conversationId },
      { skip: !conversationId },
    );

  const isLoading = isLoadingDetail || isLoadingTranscript;

  const conversationDetail = conversationDetailApi as
    | ConversationDetail
    | undefined;
  const transcriptSegments = transcriptSegmentsApi;

  // Determine which speaker is the client based on call direction
  const clientSpeakerLabel = useMemo(() => {
    if (transcriptSegments.length === 0) return null;

    const speakers = transcriptSegments.reduce<string[]>((acc, segment) => {
      if (acc.length >= 2) return acc;
      const label = segment.external_speaker_label || 'Unknown';
      if (!acc.includes(label)) {
        acc.push(label);
      }
      return acc;
    }, []);

    const isOutbound = communication.direction === 'outbound';

    if (isOutbound) {
      return speakers[0] || null;
    }
    return speakers[1] || speakers[0] || null;
  }, [transcriptSegments, communication.direction]);

  // Fetch presigned URL for audio when available
  useEffect(() => {
    const fetchAudioUrl = async () => {
      if (!conversationDetail?.audio_path) {
        setAudioUrl(null);
        return;
      }

      setIsLoadingAudio(true);
      try {
        const url = await audioService.fetchPresignedUrl(
          conversationDetail.audio_path,
        );
        setAudioUrl(url);
      } finally {
        setIsLoadingAudio(false);
      }
    };

    fetchAudioUrl();
  }, [conversationDetail?.audio_path]);

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);

    capture(CLIENT_COMMS_EVENTS.CALL_AUDIO_PLAYED, {
      [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
      [CLIENT_COMMS_PROPERTIES.COMMUNICATION_ID]: communication.id,
      [CLIENT_COMMS_PROPERTIES.DIRECTION]: communication.direction,
      [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
      [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
    });
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    capture(CLIENT_COMMS_EVENTS.CALL_TRANSCRIPT_DOWNLOADED, {
      [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
      [CLIENT_COMMS_PROPERTIES.COMMUNICATION_ID]: communication.id,
      [CLIENT_COMMS_PROPERTIES.DIRECTION]: communication.direction,
      [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
      [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
    });
    try {
      const response = await fetch(
        `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/transcript/${conversationId}/pdf/`,
        { credentials: 'include' },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `transcript-${conversationId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading && conversationId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-2 text-zinc-600">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-sm font-semibold">Loading call details...</span>
        </div>
      </div>
    );
  }

  const isInbound = communication.direction === 'inbound';
  const isMissed =
    !communication.duration_seconds || communication.duration_seconds < 10;
  const duration = communication.duration_seconds || 0;
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Determine icon based on call type
  const getCallIcon = () => {
    if (isMissed && isInbound) {
      return {
        Icon: PhoneMissed,
        bgColor: 'bg-red-50',
        iconColor: 'text-red-500',
      };
    }
    if (isInbound) {
      return {
        Icon: PhoneIncoming,
        bgColor: 'bg-primary/10',
        iconColor: 'text-primary',
      };
    }
    return {
      Icon: PhoneOutgoing,
      bgColor: 'bg-primary/10',
      iconColor: 'text-primary',
    };
  };

  const { Icon: PhoneIcon, bgColor, iconColor } = getCallIcon();

  // Get call title from AI summary or default
  const getCallTitle = () => {
    if (conversationDetail?.artifact_digest?.title) {
      return conversationDetail.artifact_digest.title;
    }
    if (isMissed && isInbound) {
      return 'Missed call from client';
    }
    if (isInbound) {
      return 'Incoming call from client';
    }
    return 'Outgoing call to client';
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Call header info */}
        <div className="px-6 py-5">
          <div className="flex items-start gap-4">
            <div
              className={cn(
                'flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center',
                bgColor,
              )}
            >
              <PhoneIcon className={cn('h-6 w-6', iconColor)} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-1">
                {getCallTitle()}
              </h2>
              <p className="text-sm font-semibold text-zinc-600">
                {formatDateTime(communication.timestamp)}
                {communication.duration_seconds && !isMissed && (
                  <> • {formatDuration(communication.duration_seconds)}</>
                )}
                {isMissed && <> • {isInbound ? 'Missed' : 'No answer'}</>}
              </p>
            </div>
          </div>
        </div>

        {/* Summary card */}
        {conversationDetail?.artifact_digest?.work_log_entry && (
          <div className="mx-6 mb-4 p-4 bg-zinc-50 rounded-xl border border-zinc-200">
            <h3 className="text-xs font-bold text-zinc-600 uppercase tracking-wide mb-2">
              Summary
            </h3>
            <p className="text-sm font-medium text-zinc-800 leading-relaxed">
              {conversationDetail.artifact_digest.work_log_entry}
            </p>

            {/* Outcome and Next Steps if available */}
            {conversationDetail.artifact_digest.outcome && (
              <div className="mt-4 pt-4 border-t border-zinc-200">
                <h4 className="text-xs font-bold text-primary uppercase tracking-wide mb-1">
                  Outcome
                </h4>
                <p className="text-sm font-medium text-zinc-800">
                  {conversationDetail.artifact_digest.outcome}
                </p>
              </div>
            )}

            {conversationDetail.artifact_digest.next_steps && (
              <div className="mt-3">
                <h4 className="text-xs font-bold text-primary uppercase tracking-wide mb-1">
                  Next Steps
                </h4>
                <p className="text-sm font-medium text-zinc-800">
                  {conversationDetail.artifact_digest.next_steps}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Audio player */}
        {(audioUrl || isLoadingAudio || conversationDetail?.audio_path) && (
          <div className="mx-6 mb-6 p-4 bg-zinc-100 rounded-xl">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handlePlayPause}
                disabled={!audioUrl || isLoadingAudio}
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center transition-colors flex-shrink-0',
                  'bg-zinc-800 hover:bg-zinc-700 text-white',
                  (!audioUrl || isLoadingAudio) &&
                    'opacity-50 cursor-not-allowed',
                )}
              >
                {isLoadingAudio && <Loader2 className="h-5 w-5 animate-spin" />}
                {!isLoadingAudio && isPlaying && <Pause className="h-5 w-5" />}
                {!isLoadingAudio && !isPlaying && (
                  <Play className="h-5 w-5 ml-0.5" />
                )}
              </button>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-zinc-800">
                    {formatTime(currentTime)}
                  </span>
                  <span className="text-sm font-semibold text-zinc-600">/</span>
                  <span className="text-sm font-semibold text-zinc-600">
                    {formatDuration(duration)}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="relative h-1.5 bg-zinc-300 rounded-full overflow-hidden">
                  <div
                    className="absolute left-0 top-0 h-full bg-primary rounded-full transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                  <input
                    type="range"
                    min={0}
                    max={duration}
                    value={currentTime}
                    onChange={handleSeek}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={!audioUrl}
                  />
                </div>
              </div>
            </div>

            {audioUrl && (
              /* eslint-disable-next-line jsx-a11y/media-has-caption -- Transcript below serves as captions */
              <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={() => setIsPlaying(false)}
                onPause={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
                onTimeUpdate={handleTimeUpdate}
                className="hidden"
              />
            )}
          </div>
        )}

        {/* Transcript */}
        {transcriptSegments.length > 0 && (
          <div className="px-6 pb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-zinc-900">
                Call Transcript
              </h3>
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-sm font-semibold text-zinc-700"
                onClick={handleDownloadPdf}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download PDF
              </Button>
            </div>

            <div>
              {transcriptSegments.map((segment) => {
                const speakerLabel =
                  segment.external_speaker_label || 'Unknown';
                const isClient = speakerLabel === clientSpeakerLabel;

                return (
                  <TranscriptSegment
                    key={segment.id}
                    segment={segment}
                    isClient={isClient}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* No transcript message */}
        {transcriptSegments.length === 0 && !isMissed && (
          <div className="flex flex-col items-center justify-center py-12 text-center px-6">
            <Play className="h-10 w-10 text-zinc-300 mb-4" />
            <h3 className="font-bold text-zinc-800 mb-1">
              No transcript available
            </h3>
            <p className="text-sm font-medium text-zinc-600">
              The transcript for this call is not yet available.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallDetailPane;
